import { useAuth } from 'react-oidc-context'
import type { ReactNode } from 'react'

/**
 * Wraps protected routes. If the user has no valid token, triggers the OIDC
 * redirect to the configured identity provider. The current URL is preserved
 * in the OIDC state so the user lands back here after login.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const auth = useAuth()

  if (auth.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas">
        <span className="font-sans text-sm text-fg-secondary">Authenticating…</span>
      </div>
    )
  }

  if (auth.error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas">
        <div className="text-center">
          <p className="font-sans text-sm text-fg-secondary">Authentication error</p>
          <p className="mt-1 font-mono text-xs text-fg-secondary">{auth.error.message}</p>
          <button
            className="mt-4 font-sans text-sm underline text-fg"
            onClick={() => auth.signinRedirect()}
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  if (!auth.isAuthenticated) {
    auth.signinRedirect({ state: { returnTo: window.location.pathname + window.location.search } })
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas">
        <span className="font-sans text-sm text-fg-secondary">Redirecting to login…</span>
      </div>
    )
  }

  return <>{children}</>
}
